import { useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, Checkbox, Chip, Popover } from "@narmi/design_system";
import type Account from "byzantine/src/Account";
import cc from "classcat";
import SearchInput from "./SearchInput";
import styles from "./RecentTransactionsModule.module.scss";

interface AccountFilterChipProps {
  accounts: Account[];
  selectedAccountIds: Set<string>;
  onApply: (ids: Set<string>) => void;
}

const AccountFilterChip = ({
  accounts,
  selectedAccountIds,
  onApply
}: AccountFilterChipProps) => {
  const { l10n } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingIds, setPendingIds] = useState<Set<string>>(
    new Set(selectedAccountIds)
  );

  const filteredAccounts = useMemo(() => {
    const lower = search.toLowerCase();
    return accounts.filter((a) =>
      `${a.getName()} ${a.getMaskedNumber()}`.toLowerCase().includes(lower)
    );
  }, [accounts, search]);

  const allFilteredSelected =
    filteredAccounts.length > 0 &&
    filteredAccounts.every((a) => pendingIds.has(a.id));

  const handleSelectAll = () => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredAccounts.forEach((a) => next.delete(a.id));
      } else {
        filteredAccounts.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };

  const handleToggle = (id: string) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApply = () => {
    onApply(pendingIds);
    setIsOpen(false);
  };

  const handleClear = () => {
    setPendingIds(new Set());
    setSearch("");
  };

  return (
    <Popover
      isOpen={isOpen}
      onUserDismiss={() => setIsOpen(false)}
      content={
        <div className={styles.searchPopover}>
          <div className={styles.searchInputRow}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={l10n.getString(
                "recent-transactions-account-filter-search-placeholder"
              )}
              autoFocus={isOpen}
            />
          </div>
          <div
            className={cc([
              styles.accountList,
              filteredAccounts.length === 0 && styles.accountListEmpty
            ])}
          >
            <label className={styles.accountCheckboxRow}>
              <Checkbox
                checked={allFilteredSelected}
                onChange={handleSelectAll}
                label={l10n.getString(
                  "recent-transactions-account-filter-select-all"
                )}
              />
            </label>
            <hr className={styles.accountListDivider} />
            {filteredAccounts.map((a) => (
              <label key={a.id} className={styles.accountCheckboxRow}>
                <Checkbox
                  checked={pendingIds.has(a.id)}
                  onChange={() => handleToggle(a.id)}
                />
                <span className={styles.accountCheckboxLabel}>
                  {a.getName()}
                </span>
              </label>
            ))}
          </div>
          <hr className={styles.accountListDividerNoMargin} />
          <div className={styles.searchFooter}>
            <Button kind="plain" size="xs" onClick={handleClear}>
              {l10n.getString("recent-transactions-search-clear")}
            </Button>
            <Button size="xs" onClick={handleApply}>
              {l10n.getString("recent-transactions-search-apply")}
            </Button>
          </div>
        </div>
      }
    >
      <Chip
        label={l10n.getString("recent-transactions-filter-account")}
        kind={selectedAccountIds.size > 0 ? "primary" : "secondary"}
        count={
          selectedAccountIds.size > 0 ? selectedAccountIds.size : undefined
        }
        endIcon={isOpen ? "chevron-up" : "chevron-down"}
        onClick={() => {
          setIsOpen((prev) => {
            if (!prev) {
              setPendingIds(new Set(selectedAccountIds));
              setSearch("");
            }
            return !prev;
          });
        }}
      />
    </Popover>
  );
};

export default AccountFilterChip;
